"use client";

import React, { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
  type CarouselApi,
} from "@/app/components/ui/carousel";
import { Button } from "@/app/components/ui/button";
import type { SliderDomainT } from "@/app/types/sliders";

type HomepageSliderProps = {
  sliders: SliderDomainT[];
};

export default function HomepageSlider({ sliders }: HomepageSliderProps) {
  const [api, setApi] = React.useState<CarouselApi>();

  useEffect(() => {
    if (!api) {
      return;
    }

    const interval = setInterval(() => {
      api.scrollNext();
    }, 6000);

    return () => {
      clearInterval(interval);
    };
  }, [api]);

  // Early return if no sliders - this should be handled by the parent component
  if (!sliders || sliders.length === 0) {
    return null;
  }

  return (
    <div
      className="relative h-[400px] w-full overflow-hidden md:h-[500px]"
      role="region"
      aria-label="Hauptbilder-Slider"
    >
      <Carousel
        className="h-[400px] w-full md:h-[500px] [&>div>div]:h-full"
        opts={{ loop: true }}
        setApi={setApi}
        aria-label="Bildergalerie"
      >
        <CarouselContent className="-ml-0">
          {sliders.map((slider, index) => (
            <CarouselItem
              key={slider.id}
              className="h-[400px] pl-0 md:h-[500px]"
              aria-label={`Slide ${index + 1} von ${sliders.length}`}
            >
              <article className="relative h-full w-full">
                {/* Dark overlay */}
                <div
                  className="pointer-events-none absolute inset-0 z-10 bg-black/10"
                  aria-hidden="true"
                />
                <div
                  className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-b from-transparent to-black/60"
                  aria-hidden="true"
                />

                {/* Background image */}
                {slider.sliderImage && (
                  <div className="absolute inset-0 z-0">
                    <Image
                      src={slider.sliderImage.url}
                      alt={slider.sliderImage.alt_text ?? slider.name}
                      fill
                      className="object-cover"
                      priority={index === 0}
                      quality={index === 0 ? 100 : 85}
                      sizes="100vw"
                      fetchPriority={index === 0 ? "high" : "auto"}
                    />
                  </div>
                )}

                {/* Content overlay - bottom left */}
                <div className="relative z-30 flex h-full w-full items-end justify-start pb-9 md:pb-20">
                  <div className="container-default padding-global w-full">
                    <div className="flex flex-col gap-3 text-white">
                      <div>
                        <h2 className="heading-large font-bold">
                          {slider.name}
                        </h2>
                        {slider.description && (
                          <p className="pt-0 text-white/90">
                            {slider.description}
                          </p>
                        )}
                      </div>
                      {slider.button_link && (
                        <div className="pt-4">
                          <Button size="md" variant="secondary" asChild>
                            <Link
                              href={slider.button_link}
                              suppressHydrationWarning
                            >
                              <span className="inline-block first-letter:uppercase">
                                {slider.button_label || "Mehr Informationen"}
                              </span>
                            </Link>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Navigation arrows - positioned inside slider */}
        {sliders.length > 1 ? <SliderNavigation /> : null}
      </Carousel>
    </div>
  );
}

function SliderNavigation() {
  const { scrollPrev, scrollNext } = useCarousel();

  return (
    <>
      <button
        onClick={scrollPrev}
        className="absolute top-1/2 left-2 z-40 hidden -translate-y-1/2 cursor-pointer text-white transition-opacity hover:opacity-80 focus:opacity-100 focus:outline-none md:left-2 md:block"
        aria-label="Vorheriges Bild"
        type="button"
      >
        <ChevronLeft
          className="h-[75px] w-[75px]"
          strokeWidth={1.125}
          aria-hidden="true"
        />
      </button>
      <button
        onClick={scrollNext}
        className="absolute top-1/2 right-2 z-40 hidden -translate-y-1/2 cursor-pointer text-white transition-opacity hover:opacity-80 focus:opacity-100 focus:outline-none md:right-2 md:block"
        aria-label="Nächstes Bild"
        type="button"
      >
        <ChevronRight
          className="h-[75px] w-[75px]"
          strokeWidth={1.125}
          aria-hidden="true"
        />
      </button>
    </>
  );
}
